import { Plugin, NodeSelection } from '@tiptap/pm/state';
import { CellSelection, cellAround } from '@tiptap/pm/tables';
import { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';

type TKind = 'table' | 'image';
type TConfig = { kind: TKind; onOpen: () => void; onClose?: () => void };

const createRightClickMenuPlugin = (config: TConfig) =>
  new Plugin({
    props: {
      handleDOMEvents: {
        // Right-click opens the menu; a plain left-click anywhere in the editor dismisses it.
        // The menu mounts outside view.dom, so clicking its buttons does not trigger this.
        mousedown: (_view, event: MouseEvent) => {
          if (event.button === 0) config.onClose?.();
          return false;
        },
        contextmenu: (view, event: MouseEvent) => {
          if (event.button !== 2 || !view.editable) return false;

          const { state } = view;

          // TABLE CONTEXT MENU
          if (config.kind === 'table') {
            // Let a nested interactive node (image, inline-block, and other draggable node views)
            // handle its own right-click instead of opening the table menu on top of it.
            if (isInsideInteractiveNode(event.target, view.dom)) return false;

            let target: Node | null = event.target as Node | null;
            while (target && target !== view.dom) {
              if (['TD', 'TH'].includes(target.nodeName)) {
                event.preventDefault();

                const pos = view.posAtDOM(target, 0);
                const $pos = state.doc.resolve(pos);
                const cellPos = cellAround($pos);
                if (!cellPos) return false;

                const sel = CellSelection.create(state.doc, cellPos.pos);
                view.dispatch(state.tr.setSelection(sel));
                config.onOpen();
                return true;
              }
              target = target.parentNode;
            }
            return false;
          }

          // IMAGE CONTEXT MENU
          if (config.kind === 'image') {
            const result = view.posAtCoords({ left: event.clientX, top: event.clientY });
            if (!result) return false;

            const { state } = view;
            const $pos = state.doc.resolve(result.pos);
            const imageType = state.schema.nodes.image;
            if (!imageType) return false;

            const imagePos = getImagePosAround($pos, imageType);
            if (imagePos == null) return false;

            // Keep the browser context menu available for text (including native
            // spelling suggestions). Suppress it only after confirming that this
            // plugin will handle an actual image.
            event.preventDefault();
            view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, imagePos)));
            config.onOpen();

            return true;
          }

          return false;
        },
      },
    },
  });

// --- HELPERS ---
// A node view with its own right-click / selection menu: an image, or any draggable node view
// (inline-block, reference, reusable-block, ...) which carries `data-drag-handle`.
const isInsideInteractiveNode = (start: EventTarget | null, stop: Node): boolean => {
  let el = start as HTMLElement | null;
  while (el && el !== stop) {
    if (el.tagName === 'IMG' || el.hasAttribute?.('data-drag-handle')) return true;
    el = el.parentElement;
  }
  return false;
};

// find the selection position for an image NodeView around $pos
const getImagePosAround = ($pos: ResolvedPos, imageType: PMNode['type']): number | null => {
  // If the node after the resolved position is an image, we're already before it
  if ($pos.nodeAfter && $pos.nodeAfter.type === imageType) return $pos.pos;
  // Otherwise, check if we are right after an image
  if ($pos.nodeBefore && $pos.nodeBefore.type === imageType) return $pos.pos - $pos.nodeBefore.nodeSize;
  return null;
};

export default createRightClickMenuPlugin;
